/**
 * `PlatformStorage` on Electron — **the bridge plumbing only. T3.C owns the
 * implementation behind it.**
 *
 * Every method here is real renderer-side code calling a real, allow-listed,
 * schema-validated channel. What is missing is the main-process handler, which
 * today answers `NOT_IMPLEMENTED` through the same validated path
 * (`electron/main/ipc/registry.ts`). So a desktop caller gets a loud typed
 * error now, and T3.C makes it work by writing
 * `electron/main/services/secure-storage.ts` and registering it — without
 * touching this file, the channel list, the schemas or the preload.
 *
 * That is the point of shipping it this way rather than as a `throw`: the
 * failure the app sees today is *the same code path* that will succeed later,
 * so T3.C is implementing a handler rather than re-plumbing a boundary.
 *
 * **T3.C's contract, restated so it cannot be lost between waves:**
 * - One JSON document at `<userData>/secure-storage.v1.json`, values
 *   `safeStorage.encryptString(...)` base64. Written atomically (tmp+rename),
 *   serialised through one in-process queue.
 * - `safeStorage.isEncryptionAvailable() === false` ⇒ **fail closed** with
 *   `SECURE_STORAGE_UNAVAILABLE` (Q4 / A3.4). Never a plaintext fallback, and
 *   never localStorage: `evaluateOfflineEligibility` must be able to reach
 *   `device-session-unreadable`, which is a *correct* answer, unlike a silent
 *   plaintext write reported as `source: 'native'`.
 * - `policy` is a Keychain accessibility concept; accept and ignore it.
 */
import { bridgeFailure, unwrapBridgeResult, secureStorageBridge } from './bridge';
import type { PlatformStorage, PlatformSecureWritePolicy } from '../services/types';

export function createElectronSecureStorage(): PlatformStorage {
  const bridge = () => secureStorageBridge();

  return {
    kind: 'electron-safe-storage',

    initialize: async (): Promise<void> => {
      const info = unwrapBridgeResult('secureStorage', await bridge().initialize({}));
      if (!info.encryptionAvailable) {
        // Fail closed at initialize rather than on the first read, so boot-time
        // offline eligibility gets a truthful answer before anything depends on
        // a session it will never be able to decrypt.
        throw bridgeFailure(
          'secureStorage',
          'SECURE_STORAGE_UNAVAILABLE',
          'Electron safeStorage encryption is unavailable on this machine. Refusing to store ' +
            'the device session or provider credentials in plaintext.'
        );
      }
    },

    get: async (key: string): Promise<string | null> =>
      unwrapBridgeResult('secureStorage', await bridge().get({ key })).value,

    set: async (key: string, value: string, policy?: PlatformSecureWritePolicy): Promise<void> => {
      unwrapBridgeResult('secureStorage', await bridge().set({ key, value, policy }));
    },

    remove: async (key: string): Promise<void> => {
      unwrapBridgeResult('secureStorage', await bridge().remove({ key }));
    },

    keys: async (): Promise<string[]> =>
      unwrapBridgeResult('secureStorage', await bridge().keys({})).keys,

    clear: async (): Promise<void> => {
      unwrapBridgeResult('secureStorage', await bridge().clear({}));
    },
  };
}
